import React, {useState} from 'react';
import classnames from 'classnames';
import {Checkbox, Expandable, Text, IconButton, WithTooltip, Badge} from '@albert-io/atomic';
import './CheckboxOption.scss';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

interface CheckboxProps {
  name: string;
  description?: string;
  id: number;
  checked?: boolean;
  onClick?: any;
  isMatching?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  expanded?: boolean | null;
  setExpanded?: (e) => void;
  className?: string;
  secondaryText?: string;
  fadeText?: boolean;
}

const CheckboxOption = ({
  id,
  name,
  description,
  checked,
  onClick,
  isMatching = false,
  disabled = false,
  indeterminate,
  expanded,
  setExpanded,
  className,
  secondaryText,
  fadeText
}: // className
CheckboxProps) => {
  const checkboxId = `cd-checkbox-${id}`;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classnames(
        'cd-modal__result-item',
        {
          'cd-modal__results-matches': isMatching
        },
        className
      )}
      onClick={(e) => {
        const checkbox = document.getElementById(checkboxId) as HTMLInputElement;
        if (checkbox) {
          checkbox.click();
        }
        e.stopPropagation();
      }}
    >
      {expanded !== null ? (
        <IconButton
          onClick={setExpanded}
          size='s'
          icon={expanded ? 'chevron-down' : 'chevron-right'}
          label={expanded ? 'Collapse' : 'Expand'}
          className='u-mar-r_1'
        />
      ) : null}
      <Checkbox
        disabled={disabled}
        data-test-id={`topics-modal__result-${name}-checkbox`}
        label='check this'
        checked={checked}
        color='purple'
        size='s'
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          onClick(e);
          e.stopPropagation();
        }}
        id={checkboxId}
        indeterminate={indeterminate}
      />
      <Text
        size='m'
        className='cd-modal__result-name u-mar-l_1'
        emphasis={fadeText ? 'lowest' : undefined}
      >
        <MarkdownRendererV2 text={name} />
      </Text>
      <Text
        size='s'
        className='cd-modal__secondary-text u-mar-l_auto u-mar-r_1'
        emphasis={fadeText ? 'lowest' : undefined}
      >
        {secondaryText}
      </Text>
      {description && (
        <span>
          <WithTooltip
            content={<MarkdownRendererV2 text={description} />}
            theme='light'
            placement='left'
          >
            <IconButton
              size='s'
              icon={['far', 'info-circle']}
              className='cd-modal__result-description-tooltip'
              label='View description'
              onClick={(e) => e.stopPropagation()}
            />
          </WithTooltip>
        </span>
      )}
    </div>
  );
};

export default CheckboxOption;

interface ExpandableResultProp {
  name: string;
  id: number;
  children: React.ReactNode;
  matches: number;
  hideMatch: boolean;
  checked?: boolean;
  onSelect?: any;
  disabled?: boolean;
  description?: string;
  expanded?: boolean;
  indeterminate?: boolean;
  secondaryText?: string;
  fadeText?: boolean;
}

export const ExpandableResult = ({
  name,
  children,
  id,
  matches,
  description,
  hideMatch = false,
  disabled = false,
  onSelect = () => {},
  checked = undefined,
  expanded = false,
  indeterminate = false,
  secondaryText = '',
  fadeText = false
}: ExpandableResultProp) => {
  const [expandItem, setExpandItem] = useState(expanded);

  const buttonId = `expandable-button-${id}`;
  const expandableId = `expandable-area-${id}`;
  const checkboxId = `cd-checkbox-${id}`;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div>
      {/* eslint-disable-next-line */}
      <div
        className='u-display_flex cd-modal__result-item u-justify-content_space-between'
        onClick={() => {
          setExpandItem(!expandItem);
        }}
      >
        <div>
          <IconButton
            onClick={() => {
              setExpandItem(!expandItem);
            }}
            size='s'
            icon={expandItem ? 'chevron-down' : 'chevron-right'}
            label={expandItem ? 'Collapse' : 'Expand'}
            className='cd-modal__result-description-tooltip'
          />
          <span className='cd-modal__result-name u-pad-l_1'>
            <Checkbox
              id={checkboxId}
              disabled={disabled}
              checked={checked}
              label='check this'
              color='purple'
              size='s'
              onClick={(e) => e.stopPropagation()}
              indeterminate={indeterminate}
              onChange={(e) => {
                onSelect(e);
                e.stopPropagation();
              }}
              className='u-pad-r_1'
            />
            <Text size='m' emphasis={fadeText ? 'lowest' : undefined}>
              {name}
            </Text>
          </span>
        </div>
        <div>
          {!hideMatch && (
            <Badge className={classnames('u-mar-lr_1', {'cd-modal__results-matches': matches > 0})}>
              {matches} topics matched
            </Badge>
          )}
          <Text
            size='s'
            emphasis={fadeText ? 'lowest' : undefined}
            className='u-mar-l_auto u-mar-r_1'
          >
            {secondaryText}
          </Text>
          {description && (
            <span>
              <WithTooltip
                content={<MarkdownRendererV2 text={description} />}
                theme='light'
                placement='left'
              >
                <IconButton
                  size='s'
                  icon={['far', 'info-circle']}
                  className='cd-modal__result-description-tooltip'
                  label='View description'
                  onClick={(e) => e.stopPropagation()}
                />
              </WithTooltip>
            </span>
          )}
        </div>
      </div>
      <Expandable aria-labelledby={buttonId} expanded={expandItem} id={expandableId}>
        {children}
      </Expandable>
    </div>
  );
};

export const ExpandableResultMemo = ExpandableResult;
